import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Skeleton, Table, TableHead, TableBody, 
  TableRow, TableCell, Button, Dialog, DialogTitle, DialogContent, 
  DialogActions, FormControl, InputLabel, Select, MenuItem, Chip 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../../services/api';

// Interfaces
interface UserKycData {
    userId: number;
    username: string;
    emailHash: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface UsersResponse {
  page: number;
  maxPages: number;
  items: UserKycData[];
}

interface StatusDialogProps {
  open: boolean;
  userId: string | null;
  currentStatus: string;
  onClose: () => void;
  onUpdateStatus: (userId: string, status: string) => void;
}

// API functions
const fetchUsers = async (page: number = 1, limit: number = 9999, search?: string): Promise<UsersResponse> => {
  console.log('Fetching users with params:', { page, limit, search });
  try {
    const response = await api.get('/api/v1/admin-panel/users-kyc', {
      params: { page, limit, search },
      headers: {
        'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
      }
    });
    console.log('Raw API response:', response);
    console.log('Response data:', response.data);
    console.log('Processed data:', response.data.data);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

const updateUserStatus = async ({ userId, status }: { userId: string, status: string }) => {
  const response = await api.patch(`/api/v1/admin-panel/users/${userId}/status`, 
    { status },
    {
      headers: {
        'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
      }
    }
  );
  return response.data;
};

// Status rendering function
const renderUserStatus = (status: string) => {
  switch (status) {
    case 'active':
      return <Chip label="Активный" color="success" size="small" />;
    case 'inactive':
      return <Chip label="Приостановлен" color="warning" size="small" />;
    case 'pending':
      return <Chip label="Заблокирован" color="error" size="small" />;
    default:
      return <Chip label={status} color="default" size="small" />;
  }
};

// Status Dialog Component
const StatusDialog: React.FC<StatusDialogProps> = ({ 
  open, userId, currentStatus, onClose, onUpdateStatus 
}) => {
  const [status, setStatus] = useState(currentStatus);
  
  const handleSave = () => {
    if (userId) {
      onUpdateStatus(userId, status);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Изменить статус пользователя</DialogTitle>
      <DialogContent>
        <Box sx={{ minWidth: 400, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Статус</InputLabel>
            <Select
              value={status}
              label="Статус"
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="active">Активный</MenuItem>
              <MenuItem value="inactive">Приостановлен</MenuItem>
              <MenuItem value="pending">Заблокирован</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Component
const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  // Fetch users data
  const { data, isLoading, error, refetch } = useQuery(
    ['users', page],
    () => fetchUsers(page),
    {
      keepPreviousData: true,
      staleTime: 5000,
      onSuccess: (data) => {
        console.log('Query succeeded with data:', data);
      },
      onError: (error) => {
        console.error('Query failed with error:', error);
      }
    }
  );

  // Log whenever data changes
  React.useEffect(() => {
    console.log('Current data state:', data);
  }, [data]);

  // Update user status mutation
  const mutation = useMutation(updateUserStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setDialogOpen(false);
    },
  });

  const handleRowClick = (userId: number) => {
    navigate(`/users/${userId}`);
  };

  const handleOpenStatusDialog = (userId: number, status: string) => {
    setSelectedUserId(userId.toString());
    setSelectedStatus(status);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUserId(null);
  };

  const handleUpdateStatus = (userId: string, status: string) => {
    mutation.mutate({ userId, status });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Пользователи
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Обновить
        </Button>
      </Box>
      
      {isLoading ? (
        <Paper sx={{ p: 2 }}>
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </Paper>
      ) : error ? (
        <Paper sx={{ p: 2 }}>
          <Typography>
            Список пользователей будет доступен после подключения к Hasura GraphQL API.
            {error && <Typography component="span">Ошибка: {error.message}</Typography>}
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          {data?.items?.length ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Имя пользователя</TableCell>
                  <TableCell>Email хэш</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Дата регистрации</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((user: UserKycData) => (
                  <TableRow 
                    key={user.userId}
                    onClick={() => handleRowClick(user.userId)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <TableCell>{user.userId}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.emailHash}</TableCell>
                    <TableCell>{renderUserStatus(user.status)}</TableCell>
                    <TableCell>{new Date(user.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click when clicking the button
                          handleOpenStatusDialog(user.userId, user.status);
                        }}
                        disabled={mutation.isLoading && selectedUserId === user.userId.toString()}
                      >
                        {mutation.isLoading && selectedUserId === user.userId.toString() ? 'Обновление...' : 'Изменить'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography>Список пользователей пуст</Typography>
          )}
        </Paper>
      )}
      
      {/* Диалог изменения статуса */}
      <StatusDialog 
        open={dialogOpen}
        userId={selectedUserId}
        currentStatus={selectedStatus}
        onClose={handleCloseDialog}
        onUpdateStatus={handleUpdateStatus}
      />
    </Box>
  );
};

export default UsersPage;